/* Song catalog. To add a song: create catalog/<id>.ts with defineSong(), import it here and add it
   to CATALOG in display order, then run `pnpm songs:check`. */
import { chordInKey, DIATONIC, type Mode, parseChord } from '../../core/theory';
import aksamCayi from './catalog/aksam-cayi';
import askDurdukca from './catalog/ask-durdukca';
import birDerdimVar from './catalog/bir-derdim-var';
import buAksam from './catalog/bu-aksam';
import endulus from './catalog/endulus';
import geceTreni from './catalog/gece-treni';
import ilkAdim from './catalog/ilk-adim';
import maviBlues from './catalog/mavi-blues';
import peri from './catalog/peri';
import sahilYolu from './catalog/sahil-yolu';
import sehirIsiklari from './catalog/sehir-isiklari';
import sonbahar from './catalog/sonbahar';
import type { Song } from './types';

export type { CoverInfo, KeyName, LyricLine, Song, SongSection } from './types';
export { defineSong } from './types';

/** Display order of the song list. */
export const CATALOG: readonly Song[] = [
  ilkAdim,
  sahilYolu,
  aksamCayi,
  geceTreni,
  endulus,
  maviBlues,
  sehirIsiklari,
  sonbahar,
  buAksam,
  birDerdimVar,
  peri,
  askDurdukca,
];

export interface SongBar {
  chord: string;
  /** index into sections */
  sec: number;
}

/** A song with derived data the pages need. */
export interface PreparedSong extends Omit<Song, 'palette'> {
  tonic: number;
  mode: Mode;
  bars: SongBar[];
  palette: string[];
}

export function keyOf(song: Pick<Song, 'key'>): { tonic: number; mode: Mode } {
  const c = parseChord(song.key);
  if (!c || (c.q !== '' && c.q !== 'm')) throw new Error(`Geçersiz tonalite: ${song.key}`);
  return { tonic: c.root, mode: c.q === 'm' ? 'minor' : 'major' };
}

/** Default palette: diatonic chords of the key (no diminished), plus V in minor, plus the song's chords. */
export function defaultPalette(song: Song): string[] {
  const { tonic, mode } = keyOf(song);
  const degs = [...DIATONIC[mode], ...(mode === 'minor' ? ['V'] : [])];
  const pal = degs.map((d) => chordInKey(d, tonic, mode).sym).filter((x) => !/dim/.test(x));
  const bars = song.sections.flatMap((s) => s.bars as string[]);
  return Array.from(new Set([...pal, ...bars]));
}

export function prepareSong(song: Song): PreparedSong {
  const { tonic, mode } = keyOf(song);
  const bars: SongBar[] = [];
  song.sections.forEach((sec, si) => {
    for (const chord of sec.bars) bars.push({ chord, sec: si });
  });
  return { ...song, tonic, mode, bars, palette: song.palette ? [...song.palette] : defaultPalette(song) };
}

export const SONGS: readonly PreparedSong[] = CATALOG.map(prepareSong);

export const songById = (id: string): PreparedSong | undefined => SONGS.find((s) => s.id === id);
